import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useRoutes } from '../hooks/useRoutes';
import type { RouteWithSlug } from '../lib/api';

/**
 * Routes Page (Phase 2.1)
 *
 * Browse all hiking routes with filtering by country and terrain.
 * Enables SEO for organic "West Highland Way gear" etc. searches.
 */

type TerrainFilter = 'all' | 'easy' | 'moderate' | 'technical';
type CountryFilter =
  | 'all'
  // UK
  | 'scotland'
  | 'england'
  | 'wales'
  // Alps
  | 'france'
  | 'switzerland'
  | 'italy'
  // Scandinavia
  | 'norway'
  | 'sweden'
  // Americas
  | 'usa'
  // Oceania
  | 'new_zealand';

const TERRAIN_LABELS: Record<TerrainFilter, string> = {
  all: 'All Difficulties',
  easy: 'Easy',
  moderate: 'Moderate',
  technical: 'Technical',
};

const COUNTRY_LABELS: Record<CountryFilter, string> = {
  all: 'All Countries',
  // UK
  scotland: 'Scotland',
  england: 'England',
  wales: 'Wales',
  // Alps
  france: 'France',
  switzerland: 'Switzerland',
  italy: 'Italy',
  // Scandinavia
  norway: 'Norway',
  sweden: 'Sweden',
  // Americas
  usa: 'USA',
  // Oceania
  new_zealand: 'New Zealand',
};

function formatCountry(country: string): string {
  return country.charAt(0).toUpperCase() + country.slice(1);
}

function formatTerrain(terrain: string): string {
  return terrain.charAt(0).toUpperCase() + terrain.slice(1);
}

function RouteCard({ route }: { route: RouteWithSlug }) {
  const durationText = route.typical_days_min === route.typical_days_max
    ? `${route.typical_days_min} days`
    : `${route.typical_days_min}-${route.typical_days_max} days`;

  return (
    <Link
      to={`/routes/${route.id}`}
      className="block bg-neutral-900 border border-neutral-800 rounded-lg p-5 hover:border-orange-500/50 transition-all group"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-neutral-500 uppercase tracking-wider">
              {formatCountry(route.country)}
              {route.region && ` · ${route.region}`}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded ${
              route.terrain === 'technical'
                ? 'bg-red-500/10 text-red-400'
                : route.terrain === 'moderate'
                ? 'bg-yellow-500/10 text-yellow-400'
                : 'bg-green-500/10 text-green-400'
            }`}>
              {formatTerrain(route.terrain)}
            </span>
          </div>
          <h3 className="font-mono text-lg font-semibold text-white group-hover:text-orange-500 transition-colors">
            {route.name}
          </h3>
        </div>

        <div className="flex flex-wrap gap-4 sm:gap-6 text-sm">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-0.5">Distance</p>
            <p className="font-mono text-white">{route.distance_km}km</p>
          </div>
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-0.5">Duration</p>
            <p className="font-mono text-white">{durationText}</p>
          </div>
          {route.elevation_gain_m && (
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-0.5">Elevation</p>
              <p className="font-mono text-white">{route.elevation_gain_m.toLocaleString()}m</p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 text-sm text-neutral-400">
        <span className="text-orange-500">Plan your kit</span>
        <svg
          className="w-4 h-4 text-orange-500 transform group-hover:translate-x-1 transition-transform"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}

export function Routes() {
  const [terrain, setTerrain] = useState<TerrainFilter>('all');
  const [country, setCountry] = useState<CountryFilter>('all');

  const {
    routes,
    total,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useRoutes({
    terrain: terrain === 'all' ? undefined : terrain,
    country: country === 'all' ? undefined : country,
  });

  // Intersection Observer for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [handleObserver]);

  // Update document title for SEO
  useEffect(() => {
    const countryName = country === 'all' ? '' : ` in ${COUNTRY_LABELS[country]}`;
    const terrainName = terrain === 'all' ? '' : ` (${TERRAIN_LABELS[terrain]})`;
    document.title = `Hiking Routes${countryName}${terrainName} | NFA`;
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, [country, terrain]);

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-8 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to search
      </Link>

      <div className="mb-8">
        <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-4">
          Hiking Routes
        </h1>
        <p className="text-neutral-400">
          Browse our curated collection of hiking routes. Each route has tailored gear recommendations
          based on terrain, weather, and specific challenges.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-8">
        <div>
          <label htmlFor="country-filter" className="block text-xs text-neutral-500 uppercase tracking-wider mb-1.5">
            Country
          </label>
          <select
            id="country-filter"
            value={country}
            onChange={(e) => setCountry(e.target.value as CountryFilter)}
            className="bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
          >
            {Object.entries(COUNTRY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="terrain-filter" className="block text-xs text-neutral-500 uppercase tracking-wider mb-1.5">
            Difficulty
          </label>
          <select
            id="terrain-filter"
            value={terrain}
            onChange={(e) => setTerrain(e.target.value as TerrainFilter)}
            className="bg-neutral-900 border border-neutral-800 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500"
          >
            {Object.entries(TERRAIN_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-center">
          <p className="text-red-400">Failed to load routes. Please try again later.</p>
        </div>
      )}

      {/* Routes List */}
      {!isLoading && !isError && (
        <>
          <p className="text-sm text-neutral-500 mb-4">
            {total} {total === 1 ? 'route' : 'routes'} found
          </p>

          {routes.length === 0 ? (
            <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-8 text-center">
              <p className="text-neutral-400">No routes match your filters.</p>
              <button
                onClick={() => {
                  setTerrain('all');
                  setCountry('all');
                }}
                className="mt-4 text-orange-500 hover:text-orange-400 text-sm"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {routes.map((route) => (
                <RouteCard key={route.id} route={route} />
              ))}

              {/* Load more trigger */}
              <div ref={loadMoreRef} className="h-4" />

              {/* Loading more indicator */}
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500" />
                </div>
              )}

              {/* End of list message */}
              {!hasNextPage && routes.length > 0 && (
                <p className="text-center text-sm text-neutral-500 py-4">
                  All routes loaded
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* SEO Content */}
      <div className="mt-16 pt-8 border-t border-neutral-800 prose prose-invert prose-sm max-w-none">
        <h2 className="font-mono text-xl font-bold text-white mb-4">Plan Your Adventure</h2>
        <p className="text-neutral-400">
          Each route has been researched to provide specific gear recommendations.
          Whether you're tackling the West Highland Way, the Cape Wrath Trail,
          or a technical scramble like the Cuillin Ridge, we'll help you pack the right kit.
        </p>
        <p className="text-neutral-400 mt-4">
          Our recommendations consider weather patterns, water availability, resupply options,
          and specific challenges like river crossings or technical terrain.
        </p>
      </div>
    </div>
  );
}
